import React, { useMemo, useRef, useLayoutEffect } from 'react'
import { Text3D } from '@react-three/drei'
import { useFrame, useThree } from '@react-three/fiber'
import * as THREE from 'three'

interface Props {
  position: [number, number, number]
  text: string
  time: number
  color?: string
  font?: string
  size?: number
  opacity?: number
}

const ThreeDInformation = ({
  position,
  text,
  time,
  color = '#1e6bff',
  font = '/fonts/nohemi.json',
  size = 0.05,
  opacity = 0.7,
}: Props): React.JSX.Element => {
  const groupRef = useRef<THREE.Group>(null)
  const text1Ref = useRef<THREE.Mesh>(null)
  const text2Ref = useRef<THREE.Mesh>(null)
  const { camera } = useThree()

  const formattedTime = useMemo(() => {
    const minutes = Math.floor(time / 60)
    const seconds = (time % 60).toFixed(2)
    return `${minutes}:${seconds.padStart(5, '0')}`
  }, [time])

  useLayoutEffect(() => {
    const centerText = (ref: React.RefObject<THREE.Mesh>) => {
      if (!ref.current) return
      ref.current.geometry.computeBoundingBox()
      const box = ref.current.geometry.boundingBox
      if (!box) return
      const center = new THREE.Vector3()
      box.getCenter(center)
      ref.current.position.sub(center)
    }

    centerText(text1Ref)
    centerText(text2Ref)

    // Décalage vertical du deuxième texte
    if (text2Ref.current && text1Ref.current) {
      const box1 = text1Ref.current.geometry.boundingBox
      if (box1) {
        const height = box1.max.y - box1.min.y
        text2Ref.current.position.y -= height * 1.4 // espace entre les deux lignes
      }
    }
  }, [text, formattedTime])

  useFrame(() => {
    if (groupRef.current) groupRef.current.lookAt(camera.position)
  })

  return (
    <group ref={groupRef} position={position}>
      <Text3D
        ref={text1Ref}
        font={font}
        size={size}
        height={size * 0.4}
        bevelEnabled
        bevelThickness={size * 0.05}
        bevelSize={size * 0.025}
        bevelSegments={3}
      >
        {`${text}m`}
        <meshStandardMaterial
          color={color}
          transparent
          opacity={opacity}
          emissive={new THREE.Color(color)}
          emissiveIntensity={0.4}
          metalness={0.1}
          roughness={0.4}
        />
      </Text3D>

      <Text3D
        ref={text2Ref}
        font={font}
        size={size * 0.8}
        height={size * 0.3}
        bevelEnabled
        bevelThickness={size * 0.04}
        bevelSize={size * 0.02}
        bevelSegments={3}
      >
        {formattedTime}
        <meshStandardMaterial
          color={color}
          transparent
          opacity={opacity * 0.9}
          emissive={new THREE.Color(color)}
          emissiveIntensity={0.3}
          metalness={0.1}
          roughness={0.4}
        />
      </Text3D>
    </group>
  )
}

export default ThreeDInformation
